import { Component, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, from, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { CreditorService } from '../creditor.service';
import { DetailedProofOfDebtRequest } from '../../interfaces/creditorDetailedProofOfDebtRequest.interface';
import { DetailedProofOfDebtResponse } from '../../interfaces/creditorDetailedProofOfDebtResponse.interface';
import { CreditorPreferentialRight } from '../../interfaces/creditorPreferentialRight.interface';

import { ClaimType } from '../../enum/claimtype.enum';
import { CreditorType } from '../../enum/creditortype.enum';
import { AccountType } from '../../enum/accounttype.enum';
import { PreferentialRightGroup } from '../../enum/preferentialrightgroup.enum';

import { NgxSpinnerService } from 'ngx-spinner';
import { ErrorModalService } from '../../modal/error.modal.service';
import { fadeInAnimation } from '../../_animations/fade-in.animation';

import { businessEmailValidator } from '../../_validators/email.validator';
import { registrationNumberValidator } from '../../_validators/registration-number.validator';
import { personalNumberValidator } from '../../_validators/personal-number.validator';
import { bicValidator } from '../../_validators/bic.validator';
import { ibanValidator } from '../../_validators/iban.validator';
import { bankAccountGroupValidator } from '../../_validators/bank-account-group.validator';
import { parseSwedishBankAccount } from '../../_validators/swedish-bank-account-adapter';
import { formatPlusgiro } from '../../_validators/plusgiro.validator';
import { formatBankgiro } from '../../_validators/bankgiro.validator';
import { addControlError, clearControlError, normalizeComparableName, normalizeComparableOrgOrPersonalNumber } from '../../_validators/validation.utils';
import { FileUploadComponent } from '../../file/fileUpload/file-upload/file-upload.component';
import { CreditorCaseSummary } from '../../interfaces/creditorCaseSummary.interface';
import { CreditorCaseDetailed } from '../../interfaces/creditorCaseDetailed.interface';
import { StoredFile } from '../../interfaces/storedfile.interface';
import { COUNTRY_OPTIONS } from '.././country-options';
import { CountryOption } from '../../interfaces/countryoption.interface';
import { CURRENCY_OPTIONS } from '.././currency-options';
import { CurrencyOption } from '../../interfaces/currencyoption.interface';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmLeaveFormModalComponent } from '../../modal/confirm-leave-form.modal.component';
import { ProofOfDebtRemark } from '../../interfaces/proofOfDebtRemark';
import { CreditorLastSubmitted } from '../../interfaces/creditorLastSubmitted.interface';

type BannerVariant = 'info' | 'success' | 'warning' | 'danger';

interface FormBanner {
  variant: BannerVariant;
  title: string;
  text: string;
}

@Component({
    selector: 'app-creditor-claim-form',
    templateUrl: './creditor-claim-form.component.html',
    styleUrls: ['./creditor-claim-form.component.scss'],
    animations: [fadeInAnimation],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreditorClaimFormComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly _swedishZipCodeValidator = Validators.pattern(/^\d{3}\s?\d{2}$/);

  public form!: FormGroup;
  public caseId = '';
  public claimId?: string;
  public caseSummary: CreditorCaseSummary | null = null;

  public isLoading = true;
  public isPreRequest = false;
  public isLateRequest = false;
  public isNew = false;
  public isReadOnly = false;
  public hasAttemptedSave = false;
  private noFilesWarningActive = false;

  public get noFilesWarningShown(): boolean {
    return this.isPreRequest && this.noFilesWarningActive && this.existingFiles.length + this.files.length === 0;
  }

  public get noFilesErrorShown(): boolean {
    return !this.isPreRequest && this.hasAttemptedSave && this.existingFiles.length + this.files.length === 0;
  }
  public creditorId = '';

  private isSaved = false;

  public preferentialRights: CreditorPreferentialRight[] = [];
  public readonly claimTypes = Object.values(ClaimType).filter(enumValue => typeof enumValue === 'number') as ClaimType[];
  public readonly creditorTypes = Object.values(CreditorType).filter(enumValue => typeof enumValue === 'number') as CreditorType[];
  public readonly accountTypes = [AccountType.Bankgiro, AccountType.Plusgiro, AccountType.Swedish, AccountType.Iban, AccountType.OtherInternational];

  public existingFiles: StoredFile[] = [];
  private pendingPreloadedFiles: File[] = [];
  private hasFailedFileCopy = false;

  public readonly countries: CountryOption[] = COUNTRY_OPTIONS;
  public isCountryDropdownOpen = false;
  public countryFilterTerm = '';
  public activeCountryIndex = -1;

  public readonly currencies: CurrencyOption[] = CURRENCY_OPTIONS;
  public isCurrencyDropdownOpen = false;
  public currencyFilterTerm = '';
  public activeCurrencyIndex = -1;

  public titleKey = '';
  public remark: ProofOfDebtRemark;
  private loadedClaim: DetailedProofOfDebtResponse | null = null;

  public bankFieldInteraction = {
    clearingBlurred: false,
    accountNumberBlurred: false,
    ibanBlurred: false,
    bicBlurred: false
  };

  private bankAccountRequiredsEnabled = false;

  private debtorName: string | null = null;
  private debtorOrgOrPersNr: string | null = null;
  private debtorIdentityUnavailable = true;

  public get debtorIdentityMissing(): boolean {
    return !this.isReadOnly && this.debtorIdentityUnavailable;
  }

  @ViewChild(FileUploadComponent) fileUpload!: FileUploadComponent;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly creditorService: CreditorService,
    private readonly spinner: NgxSpinnerService,
    private readonly errorModalService: ErrorModalService,
    private readonly translate: TranslateService,
    private readonly modalService: NgbModal,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnInit(): void {
    this.caseId = this.route.snapshot.paramMap.get('id') ?? '';
    this.claimId = this.route.snapshot.paramMap.get('claimId') || undefined;

    const navigation = this.router.getCurrentNavigation();
    this.caseSummary = navigation?.extras?.state?.['caseSummary'] ?? history.state?.caseSummary ?? null;
    this.creditorId = navigation?.extras?.state?.['creditorId'] ?? history.state?.creditorId ?? '';

    this.initializeForm();
    this.setupBankAccountReactivity();
    this.resolveModeAndLoad();

    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get files() {
    return this.fileUpload?.files ?? [];
  }

  public get selectedCountryCode(): string {
    return this.form.get('contactInformation.countryCode')?.value || 'SE';
  }

  public get selectedCountry(): CountryOption | undefined {
    return this.countries.find(c => c.code === this.selectedCountryCode);
  }

  public get filteredCountries(): CountryOption[] {
    const term = this.countryFilterTerm.trim().toLowerCase();

    if (!term) {
      return this.countries;
    }

    return this.countries.filter(country =>
      country.name.toLowerCase().includes(term) ||
      country.code.toLowerCase().includes(term)
    );
  }

  public get selectedCurrencyCode(): string {
    return this.form.get('currency')?.value || 'SEK';
  }

  public get selectedCurrency(): CurrencyOption | undefined {
    return this.currencies.find(currency => currency.code === this.selectedCurrencyCode);
  }

  public get filteredCurrencies(): CurrencyOption[] {
    const term = this.currencyFilterTerm.trim().toLowerCase();

    if (!term) {
      return this.currencies;
    }

    return this.currencies.filter(currency =>
      currency.name.toLowerCase().includes(term) ||
      currency.code.toLowerCase().includes(term)
    );
  }

  public get selectedAccountType(): AccountType | null {
    return this.toAccountType(this.form.get('bankAccount.accountType')?.value ?? null);
  }

  public get isSwedishAccountType(): boolean {
    return this.selectedAccountType === AccountType.Swedish;
  }

  public get isIbanAccountType(): boolean {
    return this.selectedAccountType === AccountType.Iban;
  }

  public get isOtherInternationalAccountType(): boolean {
    return this.selectedAccountType === AccountType.OtherInternational;
  }

  public get isPlusgiroAccountType(): boolean {
    return this.selectedAccountType === AccountType.Plusgiro;
  }

  public get isBankgiroAccountType(): boolean {
    return this.selectedAccountType === AccountType.Bankgiro;
  }

  public get hasNonOprioriteredPreferentialRight(): boolean {
    const selectedId = this.form?.get('preferentialRightId')?.value;
    if (!selectedId) return false;
    const selectedRight = this.preferentialRights.find(right => right.id === selectedId);
    return selectedRight?.group !== undefined && selectedRight.group !== PreferentialRightGroup.Oprioriterad;
  }

  public get isCreateFromPreRequestMode(): boolean {
    const url = this.router.url;
    return !!this.claimId && (url.includes('/bevakning/ny') || url.includes('/efterbevakning/ny'));
  }

  public get isNewPreRequestMode(): boolean {
    return this.isNew && this.isPreRequest;
  }

  public get isNewProofOfDebtMode(): boolean {
    return this.isNew && !this.isPreRequest && !this.isLateRequest && !this.isCreateFromPreRequestMode;
  }

  public get isNewLateProofOfDebtMode(): boolean {
    return this.isNew && !this.isPreRequest && this.isLateRequest;
  }

  public get formBanner(): FormBanner | null {
    if (this.isLoading) {
      return null;
    }

    if (this.isCreateFromPreRequestMode) {
      return {
        variant: 'success',
        title: this.translate.instant(this.isLateRequest ? 'banner.form-create-from-pre-request-late-title' : 'banner.form-create-from-pre-request-title'),
        text: this.translate.instant('banner.form-create-from-pre-request-text')
      };
    }

    if (this.isNewLateProofOfDebtMode) {
      return {
        variant: 'warning',
        title: this.translate.instant('banner.form-late-proof-of-debt-title'),
        text: this.translate.instant('banner.form-late-proof-of-debt-text')
      };
    }

    if (this.isNewProofOfDebtMode) {
      return {
        variant: 'success',
        title: this.translate.instant('banner.form-proof-of-debt-title'),
        text: this.translate.instant('banner.form-proof-of-debt-text')
      };
    }

    if (this.isNewPreRequestMode) {
      return {
        variant: 'info',
        title: this.translate.instant('banner.form-pre-request-title'),
        text: this.translate.instant('banner.form-pre-request-text')
      };
    }

    if (this.isReadOnly && this.isPreRequest) {
      return {
        variant: 'info',
        title: this.translate.instant('banner.form-view-pre-request-title'),
        text: this.translate.instant('banner.form-view-pre-request-text')
      };
    }

    if (this.isReadOnly && !this.isPreRequest) {
      return {
        variant: 'info',
        title: this.translate.instant('banner.form-view-proof-of-debt-title'),
        text: this.translate.instant('banner.form-view-proof-of-debt-text')
      };
    }

    return null;
  }

  public get accountNumberLabel(): string {
    if (this.isPlusgiroAccountType) {
      return this.translate.instant('label.plusgiro-number');
    }

    if (this.isBankgiroAccountType) {
      return this.translate.instant('label.bankgiro-number');
    }

    if (this.isOtherInternationalAccountType) {
      return this.translate.instant('label.international-account-number');
    }

    return this.translate.instant('label.kontonr');
  }

  public get neutralClearingHelpText(): string {
    return this.translate.instant('help.clearing');
  }

  public get hasAgentChecked(): boolean {
    return !!this.form?.get('hasAgent')?.value;
  }

  public get coAddressLabel(): string {
    const base = this.translate.instant('label.care-of');
    return this.hasAgentChecked
      ? `${base} (${this.translate.instant('label.agent-name-parenthetical')})`
      : base;
  }

  public get neutralAccountNumberHelpText(): string {
    if (this.isPlusgiroAccountType) {
      return this.translate.instant('help.plusgiro');
    }

    if (this.isBankgiroAccountType) {
      return this.translate.instant('help.bankgiro');
    }

    return '';
  }

  private initializeForm(): void {
    this.form = this.fb.group(
      {
        creditorName: ['', Validators.required],
        creditorOrgOrPersNr: ['', [Validators.required, this.creditorOrgOrPersNrValidator()]],
        isNotSwedish: [false],
        claimType: [ClaimType.Other, Validators.required],
        creditorType: [CreditorType.Annat, Validators.required],
        amount: [null, [Validators.required, Validators.min(0)]],
        currency: ['SEK', Validators.required],
        preferentialRightId: [null],
        isPreRequest: [false],
        isLateRequest: [false],
        comment: ['', Validators.required],
        hasAgent: [false],
        contactPersonFirstName: [''],
        contactPersonLastName: [''],
        interestAmount: [null, [Validators.min(0)]],
        interestComment: [''],
        contactInformation: this.fb.group({
          address1: ['', Validators.required],
          address2: [''],
          coAddress: [''],
          zipCode: ['', [Validators.required, Validators.maxLength(10)]],
          city: ['', Validators.required],
          countryCode: ['SE'],
          phone: [''],
          email: ['', [businessEmailValidator, Validators.required]]
        }),
        bankAccount: this.fb.group(
          {
            accountType: [AccountType.Bankgiro],
            accountHolder: [''],
            accountNumber: [''],
            clearing: [''],
            bankName: [''],
            iban: ['', [ibanValidator]],
            bic: ['', [bicValidator]],
            comment: ['']
          },
          {
            validators: [
              bankAccountGroupValidator({
                swedishTypeValue: AccountType.Swedish,
                ibanTypeValue: AccountType.Iban,
                otherInternationalTypeValue: AccountType.OtherInternational,
                plusgiroTypeValue: AccountType.Plusgiro,
                bankgiroTypeValue: AccountType.Bankgiro
              })
            ]
          }
        )
      },
      {
        validators: [
          this.interestCommentRequiredWhenAmountExistsValidator(),
          this.debtorNotAllowedAsCreditorValidator()
        ]
      }
    );

    this.form.patchValue(
      {
        isPreRequest: this.isPreRequest,
        isLateRequest: this.isLateRequest
      },
      { emitEvent: false }
    );

    this.form.get('interestAmount')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.form.get('interestComment')?.updateValueAndValidity({ emitEvent: false });
        this.form.updateValueAndValidity({ emitEvent: false });
      });

    this.form.get('interestComment')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.form.updateValueAndValidity({ emitEvent: false });
      });

    this.form.get('hasAgent')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateCoAddressValidation());

    this.form.get('isNotSwedish')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.form.get('creditorOrgOrPersNr')?.updateValueAndValidity({ emitEvent: false });
      });

    this.form.get('contactInformation.countryCode')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateZipCodeValidation());

    this.updateZipCodeValidation();
  }

  private updateCoAddressValidation(): void {
    const coAddress = this.form.get('contactInformation.coAddress');
    if (!coAddress) return;

    if (this.hasAgentChecked) {
      coAddress.addValidators(Validators.required);
    } else {
      coAddress.removeValidators(Validators.required);
    }
    coAddress.updateValueAndValidity({ emitEvent: false });
  }

  private updateZipCodeValidation(): void {
    const zipCode = this.form.get('contactInformation.zipCode');
    if (!zipCode) return;

    const countryCode = this.form.get('contactInformation.countryCode')?.value;
    if (countryCode === 'SE') {
      zipCode.addValidators(this._swedishZipCodeValidator);
    } else {
      zipCode.removeValidators(this._swedishZipCodeValidator);
    }
    zipCode.updateValueAndValidity({ emitEvent: false });
  }

  private applyClaimValidators(): void {
    if (this.isPreRequest) return;

    const accountHolder = this.form.get('bankAccount.accountHolder');
    accountHolder?.addValidators(Validators.required);
    accountHolder?.updateValueAndValidity({ emitEvent: false });

    const ocrComment = this.form.get('bankAccount.comment');
    ocrComment?.addValidators(Validators.required);
    ocrComment?.updateValueAndValidity({ emitEvent: false });

    this.bankAccountRequiredsEnabled = true;
    this.syncBankAccountRequiredValidators();
  }

  private syncBankAccountRequiredValidators(): void {
    if (!this.bankAccountRequiredsEnabled) return;

    const isSwedish = this.isSwedishAccountType;
    const isIban = this.isIbanAccountType;
    const isOtherInternational = this.isOtherInternationalAccountType;
    const isPlusgiro = this.isPlusgiroAccountType;
    const isBankgiro = this.isBankgiroAccountType;

    this.setBankFieldRequired('bankAccount.clearing', isSwedish);
    this.setBankFieldRequired(
      'bankAccount.accountNumber',
      isSwedish || isPlusgiro || isBankgiro || isOtherInternational
    );
    this.setBankFieldRequired('bankAccount.bankName', isSwedish);
    this.setBankFieldRequired('bankAccount.iban', isIban);
    this.setBankFieldRequired('bankAccount.bic', isIban || isOtherInternational);
  }

  private setBankFieldRequired(path: string, required: boolean): void {
    const control = this.form.get(path);
    if (!control) return;

    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  private interestCommentRequiredWhenAmountExistsValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const interestAmountControl = group.get('interestAmount');
      const interestCommentControl = group.get('interestComment');

      if (!interestAmountControl || !interestCommentControl) {
        return null;
      }

      const amount = interestAmountControl.value;
      const comment = (interestCommentControl.value ?? '').toString().trim();

      const hasInterestAmount =
        amount !== null &&
        amount !== undefined &&
        amount !== '' &&
        !Number.isNaN(Number(amount)) &&
        Number(amount) > 0;

      if (hasInterestAmount && comment.length === 0) {
        interestCommentControl.setErrors({
          ...(interestCommentControl.errors ?? {}),
          requiredWhenInterestAmountExists: true
        });
        return { requiredWhenInterestAmountExists: true };
      }

      if (interestCommentControl.errors?.['requiredWhenInterestAmountExists']) {
        const { requiredWhenInterestAmountExists, ...rest } = interestCommentControl.errors;
        interestCommentControl.setErrors(Object.keys(rest).length > 0 ? rest : null);
      }

      return null;
    };
  }

  private debtorNotAllowedAsCreditorValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const creditorNameControl = group.get('creditorName');
      const creditorOrgOrPersNrControl = group.get('creditorOrgOrPersNr');

      if (this.debtorIdentityMissing) {
        this.syncDebtorMatchError(creditorNameControl, false);
        this.syncDebtorMatchError(creditorOrgOrPersNrControl, false);

        return { debtorIdentityUnavailable: true };
      }

      const normalizedDebtorName = normalizeComparableName(this.debtorName);
      const normalizedDebtorNumber = normalizeComparableOrgOrPersonalNumber(this.debtorOrgOrPersNr);

      const nameMatchesDebtor =
        !!normalizedDebtorName &&
        normalizeComparableName(creditorNameControl?.value) === normalizedDebtorName;

      const numberMatchesDebtor =
        !!normalizedDebtorNumber &&
        normalizeComparableOrgOrPersonalNumber(creditorOrgOrPersNrControl?.value) === normalizedDebtorNumber;

      this.syncDebtorMatchError(creditorNameControl, nameMatchesDebtor);
      this.syncDebtorMatchError(creditorOrgOrPersNrControl, numberMatchesDebtor);

      return nameMatchesDebtor || numberMatchesDebtor
        ? { debtorNotAllowedAsCreditor: true }
        : null;
    };
  }

  private syncDebtorMatchError(control: AbstractControl | null, matchesDebtor: boolean): void {
    const alreadyMarked = !!control?.errors?.['debtorNotAllowedAsCreditor'];

    if (matchesDebtor === alreadyMarked) {
      return;
    }

    if (matchesDebtor) {
      addControlError(control, 'debtorNotAllowedAsCreditor');
    } else {
      clearControlError(control, 'debtorNotAllowedAsCreditor');
    }
  }

  private resolveModeAndLoad(): void {
    this.isLoading = true;
    this.spinner.show();

    const url = this.router.url;

    if (url.includes('/anmalan/ny')) {
      this.isPreRequest = true;
      this.isNew = true;
      this.isLateRequest = false;
      this.isReadOnly = false;
      this.applyReadOnlyState();
      this.titleKey = 'title.borgenar-anmalan-ny';
      this.loadPreferentialRights();
      return;
    }

    if (url.includes('/bevakning/ny')) {
      this.isPreRequest = false;
      this.isNew = true;
      this.isLateRequest = false;
      this.isReadOnly = false;
      this.applyReadOnlyState();
      this.applyClaimValidators();
      this.titleKey = 'title.borgenar-bevakning-ny';
      this.loadPreferentialRights(!!this.claimId);
      return;
    }

    if (url.includes('/efterbevakning/ny')) {
      this.isPreRequest = false;
      this.isNew = true;
      this.isLateRequest = true;
      this.isReadOnly = false;
      this.applyReadOnlyState();
      this.applyClaimValidators();
      this.titleKey = 'title.borgenar-efterbevakning-ny';
      this.loadPreferentialRights(!!this.claimId);
      return;
    }

    if (url.includes('/anmalan') && this.claimId) {
      this.isPreRequest = true;
      this.isNew = false;
      this.isLateRequest = false;
      this.isReadOnly = true;
      this.applyReadOnlyState();
      this.titleKey = 'title.borgenar-anmalan';
      this.loadPreferentialRights(true);
      return;
    }

    if (this.claimId) {
      this.isPreRequest = false;
      this.isNew = false;
      this.isLateRequest = false;
      this.isReadOnly = true;
      this.applyReadOnlyState();
      this.applyClaimValidators();
      this.titleKey = 'title.borgenar-bevakning';
      this.loadPreferentialRights(true);
      return;
    }

    this.finishLoading();
  }

  private setupBankAccountReactivity(): void {
    const bankAccountGroup = this.form.get('bankAccount');
    const accountTypeControl = this.form.get('bankAccount.accountType');
    const clearingControl = this.form.get('bankAccount.clearing');
    const accountNumberControl = this.form.get('bankAccount.accountNumber');
    const bankNameControl = this.form.get('bankAccount.bankName');
    const ibanControl = this.form.get('bankAccount.iban');
    const bicControl = this.form.get('bankAccount.bic');

    accountTypeControl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((rawType: AccountType | string | null) => {
        const accountType = this.toAccountType(rawType);

        this.bankFieldInteraction = {
          clearingBlurred: false,
          accountNumberBlurred: false,
          ibanBlurred: false,
          bicBlurred: false
        };

        clearingControl?.setErrors(null);
        accountNumberControl?.setErrors(null);
        bankNameControl?.setErrors(null);
        ibanControl?.setErrors(null);
        bicControl?.setErrors(null);

        if (accountType !== AccountType.Swedish) {
          clearingControl?.setValue('', { emitEvent: false });
          bankNameControl?.setValue('', { emitEvent: false });
        }

        if (accountType !== AccountType.Iban) {
          ibanControl?.setValue('', { emitEvent: false });
        }

        if (accountType !== AccountType.Iban && accountType !== AccountType.OtherInternational) {
          bicControl?.setValue('', { emitEvent: false });
        }

        clearingControl?.markAsPristine();
        clearingControl?.markAsUntouched();
        accountNumberControl?.markAsPristine();
        accountNumberControl?.markAsUntouched();
        bankNameControl?.markAsPristine();
        bankNameControl?.markAsUntouched();
        ibanControl?.markAsPristine();
        ibanControl?.markAsUntouched();
        bicControl?.markAsPristine();
        bicControl?.markAsUntouched();

        this.syncBankAccountRequiredValidators();

        bankAccountGroup?.updateValueAndValidity({ emitEvent: false });
      });

    accountNumberControl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        bankAccountGroup?.updateValueAndValidity({ emitEvent: false });

        if (this.isSwedishAccountType) {
          const bankName = this.form.get('bankAccount.bankName');
          bankName?.setValue('', { emitEvent: false });
        }
      });

    clearingControl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isSwedishAccountType) {
          const bankName = this.form.get('bankAccount.bankName');
          bankName?.setValue('', { emitEvent: false });
          bankAccountGroup?.updateValueAndValidity({ emitEvent: false });
        }
      });

    ibanControl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isIbanAccountType) {
          bankAccountGroup?.updateValueAndValidity({ emitEvent: false });
        }
      });

    bicControl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isIbanAccountType || this.isOtherInternationalAccountType) {
          bankAccountGroup?.updateValueAndValidity({ emitEvent: false });
        }
      });
  }

  private loadPreferentialRights(loadClaimAfter = false): void {
    const preferentialRights$ = this.creditorService.getPreferentialRights().pipe(
      catchError(() => {
        this.errorModalService.open(
          this.translate.instant('modal.unknown-error'),
          this.translate.instant('error.could-not-fetch-preferential-rights')
        );

        return of<CreditorPreferentialRight[]>([]);
      })
    );
    const lastSubmitted$ = !loadClaimAfter && this.isNew
      ? this.creditorService.getCreditorLastSubmitted().pipe(catchError(() => of(null)))
      : of(null);
    const detailedCase$ = this.caseId && !this.isReadOnly
      ? this.creditorService.getDetailedCreditorCase(this.caseId).pipe(catchError(() => of(null)))
      : of(null);

    forkJoin({ preferentialRights: preferentialRights$, lastSubmitted: lastSubmitted$, detailedCase: detailedCase$ })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (loadClaimAfter) {
            this.loadClaim();
          } else {
            this.finishLoading();
          }
        })
      )
      .subscribe({
        next: ({ preferentialRights, lastSubmitted, detailedCase }) => {
          this.preferentialRights = preferentialRights.sort((a, b) => a.prioOrder - b.prioOrder);
          this.applyDebtorFromCase(detailedCase);
          if (lastSubmitted) {
            this.applyDefaultsToForm(lastSubmitted);
          }
        }
      });
  }

  private applyDebtorFromCase(detailedCase: CreditorCaseDetailed | null): void {
    this.debtorName = detailedCase?.caseName?.trim() || null;
    this.debtorOrgOrPersNr = detailedCase?.caseOrgNr?.trim() || null;
    this.debtorIdentityUnavailable = !this.debtorName && !this.debtorOrgOrPersNr;

    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private applyDefaultsToForm(defaults: CreditorLastSubmitted): void {
    this.form.patchValue(
      {
        creditorName: defaults.creditorName || '',
        creditorOrgOrPersNr: defaults.creditorOrgOrPersNr || '',
        ...(defaults.claimType != null && { claimType: defaults.claimType }),
        creditorType: defaults.creditorType ?? null,
        isNotSwedish: !!defaults.isNotSwedish,
        hasAgent: !!defaults.hasAgent,
        contactPersonFirstName: defaults.contactPersonFirstName || '',
        contactPersonLastName: defaults.contactPersonLastName || '',
      },
      { emitEvent: false }
    );

    if (defaults.contactInformation) {
      this.form.get('contactInformation')?.patchValue(
        {
          address1: defaults.contactInformation.address1 || '',
          address2: defaults.contactInformation.address2 || '',
          coAddress: defaults.contactInformation.coAddress || '',
          zipCode: defaults.contactInformation.zipCode || '',
          city: defaults.contactInformation.city || '',
          countryCode: defaults.contactInformation.countryCode || 'SE',
          phone: defaults.contactInformation.phone || '',
          email: defaults.contactInformation.email || ''
        },
        { emitEvent: false }
      );
      this.updateZipCodeValidation();
    }

    if (defaults.bankAccount) {
      this.form.get('bankAccount')?.patchValue(
        {
          accountType: defaults.bankAccount.accountType ?? null,
          accountHolder: defaults.bankAccount.accountHolder || '',
          accountNumber: defaults.bankAccount.accountNumber || '',
          clearing: defaults.bankAccount.clearing || '',
          bankName: defaults.bankAccount.bankName || '',
          iban: defaults.bankAccount.iban || '',
          bic: defaults.bankAccount.bic || '',
          comment: defaults.bankAccount.comment || ''
        },
        { emitEvent: false }
      );

      const accountTypeControl = this.form.get('bankAccount.accountType');
      accountTypeControl?.setValue(accountTypeControl.value, { emitEvent: true });
    }

    this.updateCoAddressValidation();
  }


  private loadClaim(): void {
    if (!this.claimId || !this.creditorId) {
      this.finishLoading();
      return;
    }

    this.creditorService.getDetailedProofOfDebt(this.caseId, this.claimId, this.creditorId)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((claim: DetailedProofOfDebtResponse) => {
          this.applyClaimToForm(claim);

          if (this.isCreateFromPreRequestMode && this.existingFiles.length > 0) {
            return this.downloadFilesFromSourceClaim(this.existingFiles);
          }

          return of(null);
        }),
        finalize(() => {
          this.finishLoading();
          setTimeout(() => this.flushPreloadedFiles(), 0);
        })
      )
      .subscribe({
        error: () => {
          this.errorModalService.open(
            this.translate.instant('modal.unknown-error'),
            this.translate.instant('error.could-not-fetch-claim')
          );
        }
      });
  }

    private applyClaimToForm(claim: DetailedProofOfDebtResponse): void {
    this.loadedClaim = claim;
    this.existingFiles = claim.files ?? [];
    this.remark = claim.remark;

    this.form.patchValue(
      {
        creditorName: claim.creditorName || '',
        creditorOrgOrPersNr: claim.creditorOrgOrPersNr || '',
        claimType: claim.claimType ?? null,
        creditorType: claim.creditorType ?? null,
        amount: claim.amount ?? null,
        currency: claim.currency || 'SEK',
        preferentialRightId: claim.preferentialRightId || null,
        comment: claim.comment || '',
        isPreRequest: !!claim.isPreRequest,
        isLateRequest: !!claim.isLateRequest,
        hasAgent: !!claim.hasAgent,
        isNotSwedish: !!claim.isNotSwedish,
        contactPersonFirstName: claim.contactPersonFirstName || '',
        contactPersonLastName: claim.contactPersonLastName || '',
        interestAmount: claim.interestAmount ?? null,
        interestComment: claim.interestComment || ''
      },
      { emitEvent: false }
    );

    if (claim.contactInfo) {
      this.form.get('contactInformation')?.patchValue(
        {
          address1: claim.contactInfo.address1 || '',
          address2: claim.contactInfo.address2 || '',
          coAddress: claim.contactInfo.coAddress || '',
          zipCode: claim.contactInfo.zipCode || '',
          city: claim.contactInfo.city || '',
          countryCode: claim.contactInfo.countryCode || 'SE',
          phone: claim.contactInfo.phone || '',
          email: claim.contactInfo.email || ''
        },
        { emitEvent: false }
      );
      this.updateZipCodeValidation();
    }

    if (claim.bankAccount) {
      this.form.get('bankAccount')?.patchValue(
        {
          accountType: claim.bankAccount.accountType ?? null,
          accountHolder: claim.bankAccount.accountHolder || '',
          accountNumber: claim.bankAccount.accountNumber || '',
          clearing: claim.bankAccount.clearing || '',
          bankName: claim.bankAccount.bankName || '',
          iban: claim.bankAccount.iban || '',
          bic: claim.bankAccount.bic || '',
          comment: claim.bankAccount.comment || ''
        },
        { emitEvent: false }
      );

      const accountTypeControl = this.form.get('bankAccount.accountType');
      accountTypeControl?.setValue(accountTypeControl.value, { emitEvent: true });
    }

    this.form.get('bankAccount')?.updateValueAndValidity({ emitEvent: false });
    this.updateCoAddressValidation();
    this.applyReadOnlyState();
  }

  private downloadFilesFromSourceClaim(files: StoredFile[]): Observable<void> {
    this.hasFailedFileCopy = false;
    const downloadObservables = files
      .filter(file => !!file.id && !!file.fileName)
      .map(storedFile =>
        this.creditorService.getPublishedFileStream(storedFile.id!, this.caseId, false, this.creditorId).pipe(
          map(blob => new File([blob], storedFile.fileName!, { type: blob.type })),
          catchError(() => {
            this.hasFailedFileCopy = true;
            return of(null);
          })
        )
      );

    if (downloadObservables.length === 0)
      return of(undefined);

    return forkJoin(downloadObservables).pipe(
      map(downloaded => {
        this.pendingPreloadedFiles = downloaded.filter((file): file is File => file !== null);
      })
    );
  }

  private flushPreloadedFiles(): void {
    if (this.hasFailedFileCopy) {
      this.errorModalService.open(
        this.translate.instant('modal.unknown-error'),
        this.translate.instant('error.could-not-copy-files')
      );
      this.hasFailedFileCopy = false;
    }

    if (this.pendingPreloadedFiles.length === 0 || !this.fileUpload) {
      if (this.isCreateFromPreRequestMode) {
        this.existingFiles = [];
      }
      return;
    }

    this.fileUpload.addFiles(this.pendingPreloadedFiles);
    this.pendingPreloadedFiles = [];
    this.existingFiles = [];
  }

  public openCountryDropdown(): void {
    if (this.isReadOnly) {
      return;
    }

    this.isCountryDropdownOpen = true;
    this.countryFilterTerm = '';
    this.activeCountryIndex = this.countries.findIndex(c => c.code === this.selectedCountryCode);

    if (this.activeCountryIndex < 0) {
      this.activeCountryIndex = 0;
    }

    setTimeout(() => {
      const el = document.querySelector('.country-combobox__input') as HTMLInputElement | null;
      el?.focus();
    });
  }

  public onCountrySearch(value: string): void {
    this.countryFilterTerm = value;
    this.activeCountryIndex = this.filteredCountries.length > 0 ? 0 : -1;
  }

  public selectCountry(country: CountryOption): void {
    this.form.get('contactInformation.countryCode')?.setValue(country.code);
    this.form.get('contactInformation.countryCode')?.markAsDirty();
    this.form.get('contactInformation.countryCode')?.markAsTouched();

    this.isCountryDropdownOpen = false;
    this.countryFilterTerm = '';
    this.activeCountryIndex = -1;
  }

  public onCountryBlur(): void {
    setTimeout(() => {
      this.isCountryDropdownOpen = false;
      this.countryFilterTerm = '';
      this.activeCountryIndex = -1;
    }, 150);
  }

  public onCountryKeydown(event: KeyboardEvent): void {
    const countries = this.filteredCountries;

    if (!countries.length) {
      if (event.key === 'Escape') {
        this.isCountryDropdownOpen = false;
        this.countryFilterTerm = '';
        this.activeCountryIndex = -1;
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeCountryIndex = Math.min(this.activeCountryIndex + 1, countries.length - 1);
      return; 
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeCountryIndex = Math.max(this.activeCountryIndex - 1, 0);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      if (this.activeCountryIndex >= 0) {
        this.selectCountry(countries[this.activeCountryIndex]);
      }
      return;
    }

    if (event.key === 'Escape') {
      this.isCountryDropdownOpen = false;
      this.countryFilterTerm = '';
      this.activeCountryIndex = -1;
    }
  }

  public getCountryFlagClass(countryCode: string | null | undefined): string {
    if (!countryCode) {
      return '';
    }

    return `flag-icon flag-icon-${countryCode.toLowerCase()} flag-icon-squared`;
  }

  public trackByCountryCode(_: number, country: CountryOption): string {
    return country.code;
  }

  public openCurrencyDropdown(): void {
    if (this.isReadOnly) {
      return;
    }

    this.isCurrencyDropdownOpen = true;
    this.currencyFilterTerm = '';
    this.activeCurrencyIndex = this.currencies.findIndex(currency => currency.code === this.selectedCurrencyCode);

    if (this.activeCurrencyIndex < 0) {
      this.activeCurrencyIndex = 0;
    }

    setTimeout(() => {
      const inputElement = document.querySelector('.currency-combobox__input') as HTMLInputElement | null;
      inputElement?.focus();
      this.scrollActiveCurrencyOptionIntoView();
    });
  }

  public onCurrencySearch(value: string): void {
    this.currencyFilterTerm = value;
    this.activeCurrencyIndex = this.filteredCurrencies.length > 0 ? 0 : -1;
  }

  public selectCurrency(currency: CurrencyOption): void {
    this.form.get('currency')?.setValue(currency.code);
    this.form.get('currency')?.markAsDirty();
    this.form.get('currency')?.markAsTouched();

    this.closeCurrencyDropdown();
  }

  public onCurrencyBlur(): void {
    setTimeout(() => {
      this.closeCurrencyDropdown();
    }, 150);
  }

  public onCurrencyKeydown(event: KeyboardEvent): void {
    const currencies = this.filteredCurrencies;

    if (!currencies.length) {
      if (event.key === 'Escape') {
        this.closeCurrencyDropdown();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeCurrencyIndex = Math.min(this.activeCurrencyIndex + 1, currencies.length - 1);
      this.scrollActiveCurrencyOptionIntoView();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeCurrencyIndex = Math.max(this.activeCurrencyIndex - 1, 0);
      this.scrollActiveCurrencyOptionIntoView();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      if (this.activeCurrencyIndex >= 0) {
        this.selectCurrency(currencies[this.activeCurrencyIndex]);
      }
      return;
    }

    if (event.key === 'Escape') {
      this.closeCurrencyDropdown();
    }
  }

  public trackByCurrencyCode(_: number, currency: CurrencyOption): string {
    return currency.code;
  }

  private closeCurrencyDropdown(): void {
    this.isCurrencyDropdownOpen = false;
    this.currencyFilterTerm = '';
    this.activeCurrencyIndex = -1;
  }

  private scrollActiveCurrencyOptionIntoView(): void {
    if (this.activeCurrencyIndex < 0) {
      return;
    }

    const activeOption = document.getElementById('currency-option-' + this.activeCurrencyIndex);
    activeOption?.scrollIntoView({ block: 'nearest' });
  }

  public save(bypassNoFilesCheck = false): void {
    if (this.isReadOnly) {
      return;
    }

    this.hasAttemptedSave = true;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity({ emitEvent: false });
      this.noFilesWarningActive = false;
      return;
    }

    if (this.existingFiles.length + this.files.length === 0) {
      if (!this.isPreRequest) {
        return;
      }
      if (!bypassNoFilesCheck) {
        this.noFilesWarningActive = true;
        return;
      }
    }

    this.noFilesWarningActive = false;
    this.isLoading = true;
    this.spinner.show();

    this.form.patchValue(
      {
        isPreRequest: this.isPreRequest,
        isLateRequest: this.isLateRequest
      },
      { emitEvent: false }
    );

    const value = this.form.getRawValue();
    const files = this.files;

    const payload: DetailedProofOfDebtRequest = {
      amount: Number(value.amount),
      currency: value.currency || 'SEK',
      creditorName: value.creditorName,
      creditorOrgOrPersNr: value.creditorOrgOrPersNr,
      claimType: value.claimType,
      creditorType: value.creditorType,
      preferentialRightId: value.preferentialRightId || null,
      contactInfo: value.contactInformation || null,
      bankAccount: value.bankAccount || null,
      comment: value.comment,
      isPreRequest: !!this.isPreRequest,
      isLateRequest: !!this.isLateRequest,
      createdBy: 'Borgenärsportalen',
      hasAgent: !!value.hasAgent,
      isNotSwedish: !!value.isNotSwedish,
      contactPersonFirstName: value.contactPersonFirstName || null,
      contactPersonLastName: value.contactPersonLastName || null,
      interestAmount: value.interestAmount ?? null,
      interestComment: value.interestComment || null,
    };

    this.creditorService.uploadProofOfDebt(this.caseId, payload, files, this.caseSummary)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.finishLoading())
      )
      .subscribe({
        next: (result) => {
          this.navigateBackAfterSave(result.claimId, result.lastSubmittedSaved, result.filesAttached);
        },
        error: () => {
          this.errorModalService.open(
            this.translate.instant('modal.unknown-error'),
            this.translate.instant('error.could-not-save')
          );
        }
      });
  }

  public saveWithoutFiles(): void {
    this.save(true);
  }

  public canDeactivate(): Observable<boolean> | boolean {
    if (this.isSaved || this.isReadOnly || !this.hasUnsavedChanges()) {
      return true;
    }

    const modalRef = this.modalService.open(ConfirmLeaveFormModalComponent, {
      centered: true,
      backdrop: 'static'
    });

    return from(modalRef.result.then((result) => result === true).catch(() => false));
  }

  @HostListener('window:beforeunload', ['$event'])
  public onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.isSaved && !this.isReadOnly && this.hasUnsavedChanges()) {
      event.preventDefault();
    }
  }

  public navigateBack(): void {
    this.router.navigate(['/borgenar/arende', this.caseId]);
  }

  public isDownloadingReceipt = false;

  public downloadReceipt(): void {
    if (!this.caseId || !this.creditorId || !this.claimId || this.isDownloadingReceipt || !this.loadedClaim) {
      return;
    }

    this.isDownloadingReceipt = true;
    const language = this.translate.getCurrentLang() || this.translate.getFallbackLang() || 'se';

    this.creditorService
      .getProofOfDebtReceipt(this.caseId, this.creditorId, this.claimId, language, this.loadedClaim.isPreRequest, this.caseSummary?.caseName, this.loadedClaim.submittedDate)
      .pipe(takeUntil(this.destroy$), finalize(() => this.isDownloadingReceipt = false))
      .subscribe({
        next: ({ blob, filename }) => {
          this.triggerBrowserDownload(blob, filename);
        },
        error: () => {
          this.errorModalService.open(
            this.translate.instant('modal.unknown-error'),
            this.translate.instant('error.download-receipt-body')
          );
        }
      });
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = objectUrl;
    downloadLink.download = filename;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(objectUrl);
  }

  private navigateBackAfterSave(newClaimId: string, lastSubmittedSaved: boolean, filesAttached: boolean): void {
    this.isSaved = true;
    this.router.navigate(['/borgenar/arende', this.caseId], {
      state: { submitted: true, submittedClaimId: newClaimId || null, lastSubmittedSaveFailed: !lastSubmittedSaved, filesNotAttached: filesAttached === false }
    });
  }

  public onClearingBlur(): void {
    this.bankFieldInteraction.clearingBlurred = true;
    this.form.get('bankAccount.clearing')?.markAsTouched();
    this.form.get('bankAccount')?.updateValueAndValidity({ emitEvent: false });
  }

  public onAccountNumberBlur(): void {
    this.bankFieldInteraction.accountNumberBlurred = true;

    const bankAccountGroup = this.form.get('bankAccount');
    const accountNumberControl = this.form.get('bankAccount.accountNumber');
    const clearingControl = this.form.get('bankAccount.clearing');
    const bankNameControl = this.form.get('bankAccount.bankName');

    accountNumberControl?.markAsTouched();
    bankAccountGroup?.updateValueAndValidity({ emitEvent: false });

    if (this.isPlusgiroAccountType) {
      const raw = (accountNumberControl?.value ?? '').toString().trim();

      if (!raw) {
        return;
      }

      if (!accountNumberControl?.errors?.['invalidPlusgiro']) {
        accountNumberControl?.setValue(formatPlusgiro(raw), { emitEvent: false });
      }

      bankAccountGroup?.updateValueAndValidity({ emitEvent: false });
      return;
    }

    if (this.isBankgiroAccountType) {
      const raw = (accountNumberControl?.value ?? '').toString().trim();

      if (!raw) {
        return;
      }

      if (!accountNumberControl?.errors?.['invalidBankgiro']) {
        accountNumberControl?.setValue(formatBankgiro(raw), { emitEvent: false });
      }

      bankAccountGroup?.updateValueAndValidity({ emitEvent: false });
      return;
    }

    if (this.isSwedishAccountType) {
      const clearing = (clearingControl?.value ?? '').toString().trim();
      const accountNumber = (accountNumberControl?.value ?? '').toString().trim();

      if (!clearing || !accountNumber) {
        bankNameControl?.setValue('', { emitEvent: false });
        return;
      }

      const parsed = parseSwedishBankAccount(clearing, accountNumber);

      if (parsed.isValid && parsed.bankName) {
        bankNameControl?.setValue(parsed.bankName, { emitEvent: false });
      } else {
        bankNameControl?.setValue('', { emitEvent: false });
      }

      bankAccountGroup?.updateValueAndValidity({ emitEvent: false });
    }
  }

  public onIbanBlur(): void {
    this.bankFieldInteraction.ibanBlurred = true;
    this.form.get('bankAccount.iban')?.markAsTouched();
    this.form.get('bankAccount')?.updateValueAndValidity({ emitEvent: false });
  }

  public onBicBlur(): void {
    this.bankFieldInteraction.bicBlurred = true;
    this.form.get('bankAccount.bic')?.markAsTouched();
    this.form.get('bankAccount')?.updateValueAndValidity({ emitEvent: false });
  }

  public hasError(path: string, errorKey?: string): boolean {
    const control = this.form.get(path);
    if (!control) {
      return false;
    }

    if (!errorKey) {
      return control.invalid && (control.dirty || control.touched || this.hasAttemptedSave);
    }

    return !!control.errors?.[errorKey] && (control.dirty || control.touched || this.hasAttemptedSave);
  }

  public shouldShowClearingFeedback(): boolean {
    const control = this.form.get('bankAccount.clearing');
    return this.isSwedishAccountType && !!control && (control.dirty || control.touched || this.hasAttemptedSave);
  }

  public shouldShowAccountNumberFeedback(): boolean {
    const control = this.form.get('bankAccount.accountNumber');
    return (
      this.isSwedishAccountType ||
      this.isPlusgiroAccountType ||
      this.isBankgiroAccountType ||
      this.isOtherInternationalAccountType
    ) && !!control && (control.dirty || control.touched || this.hasAttemptedSave);
  }

  public shouldShowIbanFeedback(): boolean {
    const control = this.form.get('bankAccount.iban');
    return this.isIbanAccountType && !!control && (control.dirty || control.touched || this.hasAttemptedSave);
  }

  public shouldShowBicFeedback(): boolean {
    const control = this.form.get('bankAccount.bic');
    return (this.isIbanAccountType || this.isOtherInternationalAccountType) &&
      !!control &&
      (control.dirty || control.touched || this.hasAttemptedSave);
  }

  public getClearingValidationMessage(): string {
    const key = this.form.get('bankAccount.clearing')?.errors?.['invalidClearingMessageKey'];
    return key
      ? this.translate.instant(key)
      : this.translate.instant('validation.invalid-clearing-default');
  }

  public getAccountNumberValidationMessage(): string {
    const errors = this.form.get('bankAccount.accountNumber')?.errors;

    const key =
      errors?.['invalidSwedishBankAccountMessageKey'] ||
      errors?.['invalidPlusgiroMessageKey'] ||
      errors?.['invalidBankgiroMessageKey'] ||
      errors?.['invalidAccountNumberMessageKey'];

    return key
      ? this.translate.instant(key)
      : this.translate.instant('validation.invalid-account-number-default');
  }

  public getBicValidationMessage(): string {
    const key = this.form.get('bankAccount.bic')?.errors?.['invalidBicMessageKey'];
    return key
      ? this.translate.instant(key)
      : this.translate.instant('validation.invalid-bic-default');
  }

  public getIbanValidationMessage(): string {
    const key = this.form.get('bankAccount.iban')?.errors?.['invalidIbanMessageKey'];
    return key
      ? this.translate.instant(key)
      : this.translate.instant('validation.invalid-iban-default');
  }

  public getBankNameMismatchMessage(): string {
    const errors = this.form.get('bankAccount.bankName')?.errors;
    const key = errors?.['bankNameMismatchMessageKey'];
    const expectedBankName = errors?.['expectedBankName'];

    if (!key) {
      return '';
    }

    return this.translate.instant(key, { bank: expectedBankName });
  }

  public getBicCountryMismatchMessage(): string {
    const errors = this.form.get('bankAccount.bic')?.errors;
    const key = errors?.['bicCountryMismatchMessageKey'];

    if (!key) {
      return '';
    }

    return this.translate.instant(key, {
      bicCountry: errors?.['bicCountry'],
      ibanCountry: errors?.['ibanCountry']
    });
  }

  private finishLoading(): void {
    this.isLoading = false;
    this.spinner.hide();
  }

  private creditorOrgOrPersNrValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? '').toString().trim();

      if (!value) {
        return null;
      }

      if (this.form?.get('isNotSwedish')?.value === true) {
        return null;
      }

      const personalResult = personalNumberValidator(control);
      if (!personalResult) {
        return null;
      }

      const orgResult = registrationNumberValidator(control);
      if (!orgResult) {
        return null;
      }

      return { invalidOrgOrPersonalNumber: true };
    };
  }

  private toAccountType(value: AccountType | string | null | undefined): AccountType | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numeric = Number(value);
    return Number.isNaN(numeric) ? null : numeric as AccountType;
  }

  private applyReadOnlyState(): void {
    if (!this.form) {
      return;
    }

    if (this.isReadOnly) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  private hasUnsavedChanges(): boolean {
  return !!this.form?.dirty || this.files.length > 0;
}
}
